import { CircularProgress } from "../CircularProgress/CircularProgress";
import { RequestTablePagination } from "../RequestTable/RequestTablePagination";
import type { UserTariffRequest } from "../../api/user";
import {
  formatTariffDate,
  formatTariffRequestAmount,
  formatTariffRequestPrice,
  formatTariffRequestTitle,
  formatTariffRequestType,
} from "../../utils/userTariffFormat";
import {
  formatCheckPacksSummary,
  formatPaymentDateTime,
  getRequestedTariffBadge,
} from "../../utils/userLimitsFormat";
import "./TariffRequestsTable.scss";

interface TariffRequestsTableProps {
  data: UserTariffRequest[];
  page: number;
  pageSize: number;
  totalPages: number;
  confirmingPayId: string | null;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onConfirm: (payId: string) => void;
}

export const TariffRequestsTable = ({
  data,
  page,
  pageSize,
  totalPages,
  confirmingPayId,
  onPageChange,
  onPageSizeChange,
  onConfirm,
}: TariffRequestsTableProps) => {
  const canGoNext = page + 1 < totalPages;

  return (
    <div className="request-table-wrapper">
      <div className="request-table-card">
        <table className="request-table request-table--tariff-requests">
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>Email</th>
              <th>Тип</th>
              <th>Состав / тариф</th>
              <th>Стоимость</th>
              <th>Количество</th>
              <th>Дата создания</th>
              <th>Статус</th>
              <th>Ссылка</th>
              <th>Срок ссылки</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr className="tariff-requests-table-empty-row">
                <td colSpan={11} className="request-table-empty">
                  Нет запросов
                </td>
              </tr>
            ) : (
              data.map((item) => {
                const payId = item.payId ?? "";
                const isConfirming = confirmingPayId === payId;
                const isChecks = item.tariffType === "checks";

                return (
                  <tr key={payId || `${item.userId}-${item.pricelistId}-${item.createdAt}`}>
                    <td>{item.userName?.trim() || "—"}</td>
                    <td>{item.userEmail?.trim() || "—"}</td>
                    <td>
                      <span
                        className={`tariff-requests-type${
                          isChecks ? " tariff-requests-type--checks" : ""
                        }`}
                      >
                        {formatTariffRequestType(item)}
                      </span>
                    </td>
                    <td>
                      <div className="tariff-requests-title-cell">
                        <span>{formatTariffRequestTitle(item)}</span>
                        {isChecks ? (
                          <span className="tariff-requests-packs">
                            {formatCheckPacksSummary(item.checkPacks)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td>{formatTariffRequestPrice(item)}</td>
                    <td>{formatTariffRequestAmount(item)}</td>
                    <td>{formatTariffDate(item.createdAt)}</td>
                    <td>{getRequestedTariffBadge(item)}</td>
                    <td>
                      {item.paymentUrl?.trim() ? (
                        <a
                          className="tariff-requests-payment-link"
                          href={item.paymentUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={item.paymentUrl}
                        >
                          {item.paymentUrl}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{formatPaymentDateTime(item.paymentUrlExpiration)}</td>
                    <td className="tariff-requests-table-actions">
                      <button
                        type="button"
                        className="btn btn-primary tariff-requests-table-confirm"
                        disabled={!payId || isConfirming}
                        onClick={() => onConfirm(payId)}
                      >
                        {isConfirming ? <CircularProgress size="sm" /> : "Подтвердить"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <RequestTablePagination
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        canGoNext={canGoNext}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};
